import * as React from "react"
import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Pagination } from "@/components/Pagination"
import { DeliveryFilters } from "@/components/runs/DeliveryFilters"
import { RevealablePhone } from "@/components/runs/RevealablePhone"
import { RunBadges } from "@/components/runs/RunBadges"
import { RunCounts } from "@/components/runs/RunCounts"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type {
  DeliveryFilterValues,
  DeliveryOutcome,
  DeliveryRow,
  Pagination as PaginationMeta,
  RunDetail,
} from "@/types/run"

const OUTCOME_TONES: Record<
  DeliveryOutcome,
  "accent" | "danger" | "muted" | "signal"
> = {
  sent: "accent",
  failed: "danger",
  skipped_opted_out: "muted",
  skipped_no_mobile: "muted",
  skipped_duplicate: "signal",
  skipped_too_long: "signal",
}

export default function RunsShow({
  run,
  deliveries,
  filters,
  pagination,
}: {
  run: RunDetail
  deliveries: DeliveryRow[]
  filters: DeliveryFilterValues
  pagination: PaginationMeta
}) {
  const description = `For ${run.organization_name}, ${
    run.started_at_label ?? "not started yet"
  }.`

  // Only worth a line, and a label on each message, once some version other
  // than English went out.
  const multilingual = run.language_counts.some((entry) => entry.language !== "English")

  return (
    <>
      <Head title={`${run.automation_name} run`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${run.automation_name} run`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="mb-4">
          <Link
            href="/runs"
            className="inline-flex items-center gap-1 text-xs text-ink-muted no-underline hover:text-ink-display"
          >
            <ArrowLeft className="h-4 w-4" />
            All history
          </Link>
        </div>

        <PageHeader
          title={
            run.setting_label
              ? `${run.automation_name} — ${run.setting_label}`
              : run.automation_name
          }
          description={description}
        />

        <div className="mt-6 space-y-6">
          <FlashNotice />

          <div className="flex flex-wrap items-center gap-2">
            <RunBadges run={run} />
          </div>

          <dl className="grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
            <Fact term="Organization">{run.organization_name}</Fact>
            {/* Named from the run's own snapshot, so it still says which setup
                this was after that setup has been removed. */}
            {run.setting_label && (
              <Fact term="Setup">{run.setting_label}</Fact>
            )}
            <Fact term="Started">{run.started_at_label ?? "—"}</Fact>
            <Fact term="Finished">
              {run.finished_at_label ?? "Still running"}
            </Fact>
            <Fact term="Took">{run.duration_label ?? "—"}</Fact>
            <Fact term="Started by">
              {run.triggered_by ?? "The daily schedule"}
            </Fact>
            <Fact term="Times shown in">{run.time_zone_label}</Fact>
          </dl>

          {/* A refused run declined before sending, so it wrote no member rows
              at all. Saying that outright beats an empty table under counts. */}
          {run.status === "refused" && (
            <div className="callout callout-danger">
              <p className="text-sm text-danger-display">
                {run.error ?? "This run was refused, so nothing was sent."}
              </p>
              <p className="mt-2 text-xs text-ink-muted">
                Nothing was sent and no member records were written. The counts
                below are what the run would have covered.
              </p>
            </div>
          )}

          {run.status === "failed" && run.error && (
            <div className="callout callout-danger">
              <p className="text-sm text-danger-display">{run.error}</p>
              <p className="mt-2 text-xs text-ink-muted">
                The run stopped part-way through. Anything already recorded
                below still happened.
              </p>
            </div>
          )}

          {run.was_dry_run && (
            <div className="callout callout-signal py-3">
              <p className="text-xs text-ink-body">
                This was a rehearsal. Nothing was sent — every row below is what a
                real run would have done at that moment.
              </p>
            </div>
          )}

          {run.redirected_to && (
            <div className="callout py-3">
              <p className="text-xs text-ink-body">
                Every text in this run was addressed to{" "}
                <strong>{run.redirected_to}</strong>, the test recipient — not
                to the members&rsquo; own numbers. That is why the Number column
                repeats.
              </p>
            </div>
          )}

          <RunCounts run={run} />

          {multilingual && (
            <p className="text-xs text-ink-muted">
              Sent by language:{" "}
              <span className="text-ink-body">
                {run.language_counts
                  .map((entry) => `${entry.language} ${entry.count.toLocaleString("en-US")}`)
                  .join(" · ")}
              </span>
            </p>
          )}

          {run.deliveries_recorded && (
            <div>
              <DeliveryFilters run={run} filters={filters} />

              <div className="mt-4">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Member</TableHeaderCell>
                      <TableHeaderCell>Local</TableHeaderCell>
                      <TableHeaderCell>Number</TableHeaderCell>
                      <TableHeaderCell>Message</TableHeaderCell>
                      <TableHeaderCell>Outcome</TableHeaderCell>
                      <TableHeaderCell>Reason</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deliveries.length === 0 ? (
                      <TableEmpty colSpan={6}>
                        {pagination.total_count === 0 &&
                        filters.outcome === "all" &&
                        !filters.q
                          ? "No members were recorded for this run."
                          : "Nobody matches these filters."}
                      </TableEmpty>
                    ) : (
                      deliveries.map((delivery) => (
                        <TableRow key={delivery.id}>
                          <TableCell nowrap>
                            {delivery.member_name ?? delivery.member_id}
                            <span className="block text-ink-muted">
                              {delivery.member_id}
                            </span>
                          </TableCell>
                          <TableCell nowrap>{delivery.local ?? "—"}</TableCell>
                          <TableCell nowrap>
                            <RevealablePhone
                              deliveryId={delivery.id}
                              masked={delivery.phone_masked}
                              revealable={delivery.phone_revealable}
                            />
                          </TableCell>
                          <TableCell className="min-w-[16rem] max-w-md">
                            {multilingual && delivery.language && (
                              <span className="mb-1 block">
                                <Badge tone="muted">{delivery.language}</Badge>
                              </span>
                            )}
                            {delivery.message_sent || (
                              <span className="text-ink-muted">
                                Nothing was addressed to them.
                              </span>
                            )}
                          </TableCell>
                          <TableCell nowrap>
                            <Badge tone={OUTCOME_TONES[delivery.outcome]}>
                              {delivery.outcome_label}
                            </Badge>
                            {delivery.sent_at_label && (
                              <span className="mt-1 block text-ink-muted">
                                {delivery.sent_at_label}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="min-w-[12rem] max-w-sm">
                            {delivery.reason ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>

                <Pagination
                  meta={pagination}
                  path={`/runs/${run.id}`}
                  label="members"
                  params={{
                    outcome: filters.outcome === "all" ? null : filters.outcome,
                    q: filters.q,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </AppShell>
    </>
  )
}

function Fact({
  term,
  children,
}: {
  term: string
  children: React.ReactNode
}) {
  return (
    <div>
      <dt className="text-ink-muted">{term}</dt>
      <dd className="mt-0.5 text-ink-body">{children}</dd>
    </div>
  )
}
